import * as React from 'react';
import {
  IconButton as IconButtonThemeUI,
  Spinner,
  IconButtonProps,
  ThemeUIStyleObject,
} from '@apia/theme';
import { icons, TIconName, TIconType } from '@apia/icons';
import { getVariant } from '@apia/theme';

export type TIconSize = number | 'Xs' | 'Sm' | 'Md' | 'Lg' | 'Xl';

export type TApiaIconButton =
  | 'icon'
  | 'icon-primary'
  | 'icon-outline'
  | 'icon-only';

export type TIconButton = Omit<IconButtonProps, 'sx'> &
  React.AriaAttributes & {
    className?: string;
    /**
     * Es posible pasar explícitamente un ícono o el nombre del ícono de
     * acuerdo a la librería de la aplicación. Ver Icon.tsx
     */
    icon: TIconType | TIconName;
    iconSize?: TIconSize;
    id?: string;
    disabled?: boolean;
    href?: string;
    isLoading?: boolean;
    isToggled?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onMouseOut?: React.MouseEventHandler<HTMLButtonElement>;
    onMouseOver?: React.MouseEventHandler<HTMLButtonElement>;
    size?: TIconSize;
    title?: string;
    variant?: string;
    'data-test-id'?: string;
  };

/**
 * Renderiza un botón con un ícono adentro.
 *
 * La variante por defecto de este componente es buttons.icon
 *
 * Si se pasa solamente size, el iconSize será
 */
export const IconButton = React.forwardRef(
  (
    {
      className,
      disabled = false,
      icon,
      iconSize: outerIconSize,
      id,
      isLoading = false,
      isToggled = false,
      href,
      onClick = () => {},
      onMouseOver = () => {},
      onMouseOut = () => {},
      size = 'Sm',
      title,
      type = 'button',
      variant = 'icon',
      ...rest
    }: TIconButton,
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const sizes = ['Xs', 'Sm', 'Md', 'Lg', 'Xl'];
    const iconSize =
      outerIconSize ?? sizes[Math.max(0, sizes.indexOf(size as string) - 1)];

    const sx: ThemeUIStyleObject = React.useMemo(() => {
      return {
        width: `icon${size}`,
        minWidth: `icon${size}`,
        height: `icon${size}`,
        minHeight: `icon${size}`,
        svg: {
          width: `icon${iconSize}`,
          minWidth: `icon${iconSize}`,
          height: `icon${iconSize}`,
          minHeight: `icon${iconSize}`,
        },
      };
    }, [iconSize, size]);

    const ActualIcon = React.useMemo(() => {
      return typeof icon === 'string' ? icons[icon] : icon;
    }, [icon]);

    return (
      <IconButtonThemeUI
        id={id}
        ref={ref}
        onClick={React.useCallback(
          (ev: React.MouseEvent<HTMLButtonElement>) => {
            if (href) window.open(href, '_blank');
            onClick?.(ev);
          },
          [href, onClick],
        )}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        {...getVariant(variant)}
        sx={sx}
        disabled={isLoading || disabled}
        className={`${className ?? ''} iconButton ${
          isToggled ? 'isToggled' : ''
        }`}
        title={title}
        aria-label={
          rest['aria-label'] ?? (rest.as !== undefined ? undefined : title)
        }
        type={rest.as !== undefined ? undefined : type}
        role={href ? 'link' : undefined}
        {...rest}
      >
        {isLoading ? <Spinner color="inherit" /> : <ActualIcon />}
      </IconButtonThemeUI>
    );
  },
);

IconButton.displayName = 'IconButton';
